import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const Dashboard = () => {
  const { role } = useAuth();
  const [dash, setDash] = useState(null);
  const [fin, setFin] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [d, f, p] = await Promise.all([
        api.get('/dashboard/'),
        api.get('/financials/').catch(() => ({ data: null })),
        api.get('/purchases/').catch(() => ({ data: [] })),
      ]);
      setDash(d.data);
      setFin(f.data);
      setPurchases(Array.isArray(p.data) ? p.data.slice(0, 8) : []);
    } catch {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const canOps = role === 'master' || role === 'group';

  const runAggregate = async () => {
    setBusy(true);
    setActionMsg('');
    try {
      const res = await api.post('/aggregations/aggregate/');
      setActionMsg(
        `Aggregated pool PKR ${money(res.data.total_amount)} · discount PKR ${money(res.data.discount_amount)}`
      );
      await load();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Aggregate failed — need purchases in recorded status');
    } finally {
      setBusy(false);
    }
  };

  const distributeLatest = async () => {
    setBusy(true);
    setActionMsg('');
    try {
      const list = await api.get('/aggregations/');
      const pending = (list.data || []).find((a) => a.status === 'aggregated');
      if (!pending) {
        setActionMsg('No aggregated batch waiting to distribute');
        return;
      }
      const res = await api.post(`/aggregations/${pending.id}/distribute/`);
      setActionMsg(`Distributed discounts for aggregation #${res.data.id}`);
      await load();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Distribute failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div>Loading dashboard…</div>;
  if (error) return <div className="error">{error}</div>;
  if (!dash) return null;

  const cards = [
    { label: 'Purchase volume', value: `PKR ${money(dash.total_purchases)}`, tip: 'Sum of member purchase totals' },
    { label: 'In aggregation', value: `PKR ${money(dash.aggregated)}`, tip: 'Amount pooled for bulk pricing' },
    { label: 'Discounts earned', value: `PKR ${money(dash.total_discounts)}`, tip: 'Savings already distributed' },
    { label: 'Active members', value: money(dash.member_count), tip: 'Users with role = member' },
  ];

  if (fin) {
    cards.push(
      { label: 'Net cost', value: `PKR ${money(fin.net_cost)}`, tip: 'Purchases minus discounts' },
      { label: 'Savings rate', value: `${money(fin.savings_rate)}%`, tip: 'Discount ÷ purchases' }
    );
  }

  return (
    <div className="dash-page">
      <div className="flex-between">
        <div>
          <h2 style={{ margin: 0 }}>Dashboard</h2>
          <p className="dash-sub">How Ace Kaatha turns group buying into measurable savings</p>
        </div>
        <button type="button" className="btn btn-outline" onClick={load}>Refresh</button>
      </div>

      <div className="dash-howto">
        <h3>Business flow</h3>
        <div className="dash-steps">
          <div className="dash-step">
            <span>1</span>
            <div>
              <strong>Record</strong>
              <p>Members (or admins) log purchases against suppliers. Status starts as <em>recorded</em>.</p>
            </div>
          </div>
          <div className="dash-step">
            <span>2</span>
            <div>
              <strong>Aggregate</strong>
              <p>Group/Master pools all recorded buys, applies active discount rules (capped at 30%).</p>
            </div>
          </div>
          <div className="dash-step">
            <span>3</span>
            <div>
              <strong>Distribute</strong>
              <p>Savings split by each member&apos;s share of the pool → Discount rows appear.</p>
            </div>
          </div>
          <div className="dash-step">
            <span>4</span>
            <div>
              <strong>Books</strong>
              <p>Journal, trial balance, tax, month-end, and assets keep the ledger honest.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="stats-grid">
        {cards.map((c) => (
          <div className="stat-card dash-card" key={c.label} title={c.tip}>
            <h3>{c.label}</h3>
            <p>{c.value}</p>
            <small>{c.tip}</small>
          </div>
        ))}
      </div>

      {canOps && (
        <div className="dash-actions">
          <div>
            <h3>Ops shortcuts</h3>
            <p>Run the live procurement cycle without leaving the dashboard.</p>
          </div>
          <div className="dash-action-btns">
            <Link to="/purchases" className="btn btn-outline">Add purchase</Link>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={runAggregate}>
              Aggregate recorded
            </button>
            <button type="button" className="btn btn-success" disabled={busy} onClick={distributeLatest}>
              Distribute discounts
            </button>
            <Link to="/aggregation" className="btn btn-outline">Aggregation desk</Link>
          </div>
          {actionMsg && <p className="dash-action-msg">{actionMsg}</p>}
        </div>
      )}

      <div className="chart-container">
        <div className="flex-between">
          <h3 style={{ margin: 0 }}>Recent purchases</h3>
          <Link to="/purchases">View all</Link>
        </div>
        {purchases.length === 0 ? (
          <p className="dash-sub">No purchases yet — soft-login as Member and add one, or use Master Admin.</p>
        ) : (
          <table className="table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Member</th>
                <th>Item</th>
                <th>Supplier</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p) => (
                <tr key={p.id}>
                  <td>{p.member_name || p.member}</td>
                  <td>{p.item}</td>
                  <td>{p.supplier_name || '—'}</td>
                  <td>PKR {money(p.total)}</td>
                  <td><span className={`badge ${p.status}`}>{p.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
